<template>
  <span v-if="visitId > 0">
    <v-btn small outlined class="ma-2" @click="open">Attempts / Reset Visit</v-btn>
    <v-dialog v-model="dialog" max-width="850" :persistent="saving">
      <v-card>
        <v-card-title>Visit Attempt History</v-card-title>
        <v-card-text>
          <v-progress-linear v-if="loading" indeterminate />
          <v-alert v-if="error" type="error">{{ error }}</v-alert>
          <template v-if="current && !loading">
            <p>Current attempt #{{ current.attempt_number }} — {{ current.status }}</p>
            <template v-if="canReset">
              <p>Reset this unfinished visit to Pending. Previous activity and photos will be preserved. Containers already collected, extras and notes stay on this visit.</p>
              <v-textarea v-model="reason" label="Reason for resetting" maxlength="2000" :disabled="saving" />
              <v-btn color="warning" :loading="saving" :disabled="saving || !reason.trim()" @click="resetVisit">Reset to Pending</v-btn>
            </template>
            <p v-if="!current.attempts.length" class="mt-4">No previous reset attempts.</p>
            <v-expansion-panels class="mt-4">
              <v-expansion-panel v-for="attempt in current.attempts" :key="attempt.id">
                <v-expansion-panel-header>Attempt #{{ attempt.attempt_number }} — {{ attempt.status }} — {{ attempt.reset_at }}</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <p>Reset by {{ attempt.reset_by }}: {{ attempt.reason }}</p>
                  <p v-if="attempt.manifest_number">Previous manifest: {{ attempt.manifest_number }}</p>
                  <p v-if="attempt.notes">Notes: {{ attempt.notes }}</p>
                  <p v-if="attempt.notes_to_driver">Driver notes: {{ attempt.notes_to_driver }}</p>
                  <v-img v-if="attempt.image" :src="imageBase + attempt.image" max-height="350" contain />
                  <div v-for="event in attempt.activity" :key="'activity-' + event.id">
                    {{ event.dateTime }} — {{ eventName(event.event_id) }} {{ event.notes }}
                  </div>
                  <p v-if="attempt.container_events.length" class="mt-3">Container activity recorded through this attempt:</p>
                  <div v-for="event in attempt.container_events" :key="'container-' + event.id">
                    {{ event.created_at }} — {{ event.event }} — {{ event.container }}
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </template>
        </v-card-text>
        <v-card-actions><v-spacer /><v-btn text :disabled="saving" @click="dialog = false">Close</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
export default {
  name: "VisitAttempts",
  props: {
    visitId: { type: [Number, String], required: true },
    imageBase: { type: String, required: true },
  },
  data() {
    return { dialog: false, loading: false, saving: false, error: "", reason: "", current: null };
  },
  computed: {
    canReset() {
      return this.current && ["On Route", "Check In", "Extras / Notes", "Closed"].includes(this.current.status);
    },
  },
  methods: {
    credentials() {
      return { driver_id: this.$store.getters.driver_id, token: this.$store.getters.token, routeschedule_id: this.visitId };
    },
    eventName(id) {
      return ({ 4: "Check In", 6: "Driver signature", 7: "Customer signature", 8: "Completed", 11: "Closed" })[id] || "Activity #" + id;
    },
    async open() {
      this.dialog = true;
      this.reason = "";
      this.current = null;
      this.error = "";
      this.loading = true;
      try {
        const response = await this.$http.post(this.$api + "visitAttempts", this.credentials());
        if (response.data.status !== "ok") throw new Error(response.data.msg);
        this.current = response.data.data;
      } catch (error) {
        this.error = (error.response && error.response.data.msg) || error.message || "Unable to load attempt history.";
      } finally {
        this.loading = false;
      }
    },
    async resetVisit() {
      this.saving = true;
      this.error = "";
      try {
        const response = await this.$http.post(this.$api + "reset", {
          ...this.credentials(),
          attempt_number: this.current.attempt_number,
          expected_status: this.current.status,
          reason: this.reason.trim(),
        });
        if (response.data.status !== "ok") throw new Error(response.data.msg);
        this.$emit("reset");
        await this.open();
      } catch (error) {
        this.error = (error.response && error.response.data.msg) || error.message || "Unable to reset this visit. Refresh the history and try again.";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
